'use client';

import Image from 'next/image';
import { cn } from '@/lib/utils';
import { skipImageOptimizer } from '@/lib/image-optimizer';
import { galleryShowsThumbnails } from '@/lib/product-gallery';
import { VideoThumbnail } from './video-thumbnail';

/**
 * The thumbnail picker for a product gallery, either in a strip beneath the
 * frame or in a rail beside it.
 *
 * BOTH frame modes get one, on different thresholds, and the CALL SITE decides
 * which threshold applies: full width from two items (`galleryShowsThumbnails`,
 * the guard below), the standard two-column frame from three
 * (`galleryShowsStandardThumbnails`). The standard frame shares its row with
 * the buy box, so its strip is added height on the page carrying the price and
 * is only worth it once the arrows and the counter stop being enough.
 *
 * Shared by the product page and both editor previews, for the same reason
 * everything else about the gallery is: three hand-written copies of a picker
 * that has to agree on which item is selected is three chances to disagree.
 *
 * WHICH SIDE. `galleryThumbnailsOnSide` decides, and the reasoning is there.
 * The short version is that every shape except page-width is bounded by the
 * height cap, so the frame is narrower than the content area and there is empty
 * margin beside it; the rail goes in that margin and costs the page no vertical
 * space, which matters on the page carrying the price and the buy button.
 *
 * BOTH VARIANTS ARE THE HORIZONTAL STRIP ON A PHONE. A rail beside the frame on
 * a 390px screen takes a quarter of the width off the picture to show two
 * thumbnails, so the side variant only becomes a rail at `md`. That is also why
 * the switch is a set of responsive classes rather than two different trees:
 * the breakpoint has no representation in React state, and a JS media query
 * would render the wrong one until it hydrated.
 *
 * The scroller does not wrap in either direction. A grid that reflowed would
 * change the page height as the seller adds images, and past a handful of items
 * a wrapped grid competes with the main frame for attention instead of
 * supporting it.
 */
export function GalleryThumbnails({
  media,
  activeIndex,
  onSelect,
  className,
  orientation = 'bottom',
}: {
  media: ReadonlyArray<{ type?: 'image' | 'video'; src: string }>;
  activeIndex: number;
  onSelect: (index: number) => void;
  className?: string;
  /**
   * 'bottom' is the strip under the frame. 'side' is the rail beside it, which
   * is still the strip below `md`. Call sites decide with
   * `galleryThumbnailsOnSide`, so all three reach the same answer.
   */
  orientation?: 'bottom' | 'side';
}) {
  // One item needs no picker: the strip would be a single thumbnail of the
  // image already filling the frame above it. `galleryShowsThumbnails` is the
  // same rule, exported so the render sites can leave the rail's column out of
  // the layout rather than holding an empty one open beside the picture.
  if (!galleryShowsThumbnails(media.length)) return null;

  const scroller = (
    // A plain group, NOT a tablist. These were `role="tablist"` / `role="tab"`,
    // which is a promise this strip does not keep: a tab controls a tabpanel by
    // aria-controls and a tablist owns arrow-key navigation with a roving
    // tabindex. There is no tabpanel here (the frame above is not one) and
    // there was no key handling, so a screen reader announced "tab 3 of 5" for
    // a relationship that did not exist and keyboard users got none of the
    // behaviour the role advertised. As buttons carrying aria-current, each one
    // is reachable by Tab on its own and says what it actually is.
    <div
      className={cn(
        'flex gap-2 overflow-x-auto pb-1',
        // The rail. `absolute inset-0` inside the relative box below is what
        // keeps a long list from growing the row: an absolutely positioned
        // child contributes no height, so the block stays exactly as tall as
        // the frame beside it and the extra thumbnails scroll instead.
        orientation === 'side'
          && 'md:absolute md:inset-0 md:flex-col md:items-center md:overflow-x-hidden md:overflow-y-auto md:pb-0',
        orientation === 'bottom' && className,
      )}
      role="group"
      aria-label="Product media"
    >
      {media.map((item, index) => {
        const selected = index === activeIndex;
        return (
          <button
            key={`${item.src}-${index}`}
            type="button"
            aria-current={selected ? 'true' : undefined}
            aria-label={`Show ${item.type === 'video' ? 'video' : 'image'} ${index + 1} of ${media.length}${selected ? ' (currently shown)' : ''}`}
            onClick={() => onSelect(index)}
            className={cn(
              // Fixed width so the strip's rhythm does not depend on the shape
              // of the images in it, and shrink-0 so they scroll rather than
              // being squeezed as more are added.
              'relative h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-card transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              selected ? 'border-ink ring-2 ring-ink' : 'hover:border-foreground/30',
            )}
          >
            {item.type === 'video' ? (
              // The poster frame, with a play badge over it. Served through the
              // image optimizer, so picking it up costs the visitor no request
              // to the video's host.
              <VideoThumbnail src={item.src} />
            ) : (
              <Image
                src={item.src}
                alt=""
                fill
                // Thumbnails are 64px; without this every one of them would be
                // requested at the default 100vw.
                sizes="64px"
                unoptimized={skipImageOptimizer(item.src)}
                // `contain`, matching the main frame: a thumbnail that crops
                // shows a different picture from the one it selects.
                className="object-contain"
              />
            )}
          </button>
        );
      })}
    </div>
  );

  if (orientation === 'bottom') return scroller;

  // The rail's own column. `relative` is what the absolutely positioned
  // scroller above is measured against, and the fixed width is mirrored by
  // GALLERY_SIDE_RAIL_REM, which is what the block's max-width adds to the
  // frame's ceiling to make room for it.
  //
  // Below `md` this is an ordinary block holding the horizontal strip, spaced
  // off the frame the way the bottom variant is.
  return (
    <div className={cn('relative mt-3 md:mt-0 md:w-[5.5rem] md:shrink-0', className)}>
      {scroller}
    </div>
  );
}
